import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { ProjectOverview } from '../../components/projects/ProjectOverview';
import { ProjectConnections } from '../../components/projects/ProjectConnections';
import { ProjectSettings } from '../../components/projects/ProjectSettings';
import { ProjectAnalytics } from '../../components/projects/ProjectAnalytics';
import { useProjects } from '../../hooks/useProjects';
import { Skeleton } from '../../components/ui/Skeleton';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  ExternalLink, 
  Settings, 
  BarChart3, 
  Plug, 
  MessageSquare,
  Sparkles,
  Loader
} from 'lucide-react';

export function ProjectDetailPage() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  const { getProject, updateProject, deleteProject } = useProjects();
  const { data: projectData, isLoading, error } = getProject(projectId || '');
  
  const project = projectData?.project;
  
  // Update tab when URL param changes
  useEffect(() => {
    if (searchParams.get('tab')) {
      setActiveTab(searchParams.get('tab') || 'overview');
    }
  }, [searchParams]);
  
  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(`/projects/${projectId}?tab=${value}`, { replace: true });
  };
  
  const handleUpdateProject = async (data: any) => {
    if (!projectId) return;
    
    try {
      await updateProject({ projectId, data });
      toast.success('Project updated successfully');
    } catch (error) {
      console.error('Failed to update project:', error);
      toast.error('Failed to update project');
    }
  };
  
  const handleDeleteProject = async () => {
    if (!projectId) return;
    
    try {
      await deleteProject(projectId);
      toast.success('Project deleted successfully');
      navigate('/projects');
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project');
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Error loading project</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">There was a problem loading the project details.</p>
          <Button onClick={() => navigate('/projects')}>
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Skeleton className="h-9 w-24" />
            <div className="h-6 w-px bg-gray-300" />
            <div>
              <Skeleton className="h-8 w-48 mb-2" />
              <Skeleton className="h-5 w-64" />
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-9 w-36" />
          </div>
        </div>
        
        {/* Tabs Skeleton */}
        <Skeleton className="h-10 w-full" />
        
        {/* Content Skeleton */}
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Project not found</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">The project you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/projects')}>
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/projects')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Projects
          </Button>
          <div className="h-6 w-px bg-gray-300 dark:bg-gray-600" />
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{project.name}</h1>
              <Badge variant={project.isActive ? 'success' : 'secondary'}>
                {project.isActive ? 'Active' : 'Inactive'}
              </Badge>
              {project.ga4Connected && (
                <Badge variant="outline" className="text-green-600 border-green-200">
                  GA4 Connected
                </Badge>
              )}
            </div>
            <div className="flex items-center space-x-4 mt-1">
              <p className="text-gray-600 dark:text-gray-300">{project.description}</p>
              {project.websiteUrl && (
                <a
                  href={project.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 flex items-center text-sm"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Visit site
                </a>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={() => navigate(`/agents/data?project=${projectId}`)}
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Ask AI Agent
          </Button>
          <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
            <Sparkles className="w-4 h-4 mr-2" />
            Generate Report
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview" className="flex items-center">
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center">
            <BarChart3 className="w-4 h-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="connections" className="flex items-center">
            <Plug className="w-4 h-4 mr-2" />
            Connections
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <ProjectOverview project={project} />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <ProjectAnalytics project={project} />
        </TabsContent>

        <TabsContent value="connections" className="space-y-6">
          <ProjectConnections project={project} />
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <ProjectSettings 
            project={project} 
            onUpdate={handleUpdateProject}
            onDelete={handleDeleteProject}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}